import { useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { IconBrandMessenger, IconCheck, IconCopy, IconSend } from "@tabler/icons-react";
import { useLocaleUrl } from "@ct-react/locale";
import { classNames, TranslatedString, useAppConfig } from "@ct-react/core";
import { Image } from "@shared/graphql";
import { SkeletonableProps } from "../../tools/components";
import RatioImage from "../medias/ratio-image";
import SpriteIcon from "../medias/sprite-icon";
import "./sharing.scss";
import ArticleResume from "../article-resume";

const transDefs = defineMessages({
  copy: { id: "components.sharing.copy-label", defaultMessage: "Copier le lien" },
  copied: { id: "components.sharing.copied", defaultMessage: "Lien copié!" },
  email: { id: "components.sharing.email", defaultMessage: "Adresse e-mail" }
});

type Props = {
  title: TranslatedString;
  address: string;
  image?: Image;
}
const Sharing = ({ loading, className, data }: SkeletonableProps<Props>) => {

  const intl = useIntl();
  const { baseUrl } = useAppConfig();
  const { currentUrl } = useLocaleUrl(baseUrl);

  const [ copySuccess, setCopySuccess ] = useState<boolean>(false);
  const url = useMemo(() => encodeURIComponent(currentUrl), [ currentUrl ]);

  const onCopy = () => navigator.clipboard.writeText(currentUrl).then(() => {
    if (copySuccess) return;
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);
  });

  const onTarget = (action: "mailto" | "sms") =>
    window.location.href = `${action}:?body=${url}`;

  const onPop = (type: "whatsapp" | "facebook") => {
    const dest = type === "facebook"
      ? `https://www.facebook.com/sharer/sharer.php?u=${url}`
      : `https://wa.me/?text=${url}`;
    window.open(dest, "_blank");
  }

  if (loading)
    return null;

  const wrapperClasses = classNames("share-content", className);

  return (
    <div className={wrapperClasses}>

      <ArticleResume data={data!} />

      <div className="action-group">
        <button type="button"
                onClick={onCopy}>
          {intl.formatMessage(transDefs.copy)}
          {copySuccess &&
            <span className="copy-success">
              <IconCheck className="cs-icon" />
              {intl.formatMessage(transDefs.copied)}
            </span>
          }
          <IconCopy />
        </button>
      </div>

      <div className="action-group">
        <button type="button"
                onClick={() => onTarget("mailto")}>
          {intl.formatMessage(transDefs.email)}
          <IconSend />
        </button>
        <button type="button"
                onClick={() => onTarget("sms")}>
          SMS
          <IconBrandMessenger />
        </button>
        <button type="button"
                onClick={() => onPop("whatsapp")}>
          WhatsApp
          <SpriteIcon hash="social.whatsapp" />
        </button>
        <button type="button"
                onClick={() => onPop("facebook")}>
          Facebook
          <SpriteIcon hash="social.facebook" />
        </button>
      </div>

    </div>);

}

export default Sharing;
